/**
 * My Assistance Requests — list of every Consumer Assistance Package the
 * consumer has drafted or submitted, with status + reference number.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import { HelpCircle, Loader2, PlusCircle, FileText, AlertTriangle, Target, CreditCard, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/assistance`;

function getDeviceId() {
  let id = localStorage.getItem("altara.device_id");
  if (!id) { id = "dev_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36).slice(-6); localStorage.setItem("altara.device_id", id); }
  return id;
}

const KIND_ICON = { complaint: FileText, scam: AlertTriangle, unfair_treatment: Target, payment_concern: CreditCard, other: HelpCircle };
const KIND_LABEL = { complaint: "Complaint", scam: "Scam concern", unfair_treatment: "Unfair treatment", payment_concern: "Payment concern", other: "Other" };
const STATUS_TONE = {
  draft: { color: "#B45309", bg: "#FEF3C7", label: "Draft" },
  submitted: { color: "#065F46", bg: "#D1FAE5", label: "Submitted" },
  acknowledged_by_institution: { color: "#065F46", bg: "#A7F3D0", label: "Acknowledged" },
  closed_by_consumer: { color: "#475569", bg: "#E2E8F0", label: "Closed" },
};

export default function WalletAssistanceList() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/requests?device_id=${getDeviceId()}`)
      .then(r => setItems(r.data || []))
      .catch(e => { setError(e?.response?.data?.detail || "Load failed"); setItems([]); });
  }, []);

  return (
    <WalletShell activeTab="safety">
      <div className="max-w-4xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="assistance-list-page">
        <header className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Consumer Assistance</div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>My Assistance Requests</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-2xl">Every package Altara has helped you send to an institution. The institution remains responsible for ongoing communication — Altara records the transfer for your audit trail.</p>
          </div>
          <Link to="/wallet/assistance/new?kind=complaint" data-testid="assistance-new-btn" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: NAVY, color: "white" }}>
            <PlusCircle className="w-4 h-4" /> New request
          </Link>
        </header>

        {items === null && (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {error && <div className="rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800 p-3">{error}</div>}

        {items && items.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center" data-testid="assistance-empty">
            <HelpCircle className="w-8 h-8 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>No requests yet</div>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">When you need help articulating a complaint, scam concern or payment issue, we&apos;ll package it here and transfer it to the institution.</p>
          </div>
        )}

        {items && items.length > 0 && (
          <div className="space-y-3" data-testid="assistance-list">
            {items.map(r => <RequestRow key={r.id} r={r} />)}
          </div>
        )}
      </div>
    </WalletShell>
  );
}

function RequestRow({ r }) {
  const Icon = KIND_ICON[r.kind] || HelpCircle;
  const tone = STATUS_TONE[r.status] || STATUS_TONE.draft;
  return (
    <div data-testid={`assistance-row-${r.id}`} className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 lg:p-5">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22`, color: NAVY }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-[13px] font-semibold" style={{ color: NAVY }}>{KIND_LABEL[r.kind] || r.kind}</span>
            <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: tone.bg, color: tone.color }}>{tone.label}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
            <Building2 className="w-3 h-3" />
            <span>{r.institution_name || "Institution not yet set"}</span>
            {r.reference_number && <span className="font-mono text-slate-700">· Ref {r.reference_number}</span>}
          </div>
          {r.what_happened && <div className="mt-2 text-[12px] text-slate-700 line-clamp-2">{r.what_happened}</div>}
          <div className="mt-2 text-[10px] text-slate-500">
            {r.submitted_at ? <>Submitted {new Date(r.submitted_at).toLocaleDateString()}</> : <>Drafted {new Date(r.created_at).toLocaleDateString()}</>}
            {" · "}{r.evidence?.length || 0} evidence item{(r.evidence?.length || 0) === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      {r.status === "submitted" && (
        <div className="mt-3 rounded-lg p-3 text-[11px] leading-relaxed" style={{ background: `${GOLD}12`, color: NAVY }}>
          Successfully submitted — important for your audit trail. <strong>The institution will contact you directly.</strong>
        </div>
      )}
    </div>
  );
}
